/*
 * Copyright (c) CIRROM 2026.
 */

import React, { useEffect, useRef, useState } from "react";
import Typography from "@mui/material/Typography";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { LedgerField, summarizeLedger } from "./helpers/normalize";
import { LedgerRow, LedgerRowActions } from "./LedgerRow";
import { rampIconColor } from "./helpers/rampColor";
import { AppliedChange } from "../../api/suggestedchanges";
import { AppliedChangesList } from "./AppliedChangesList";
import classes from "./css/recordReview.module.css";

/**
 * The triage story in one line: volume, how much needs a human choice, how
 * much is low-risk fill - and where approvals actually land, because a write
 * to the system of record should never be abstract.
 */
const buildSummary = (fields: LedgerField[], crmLabel?: string): string => {
  const { total, contested, fills } = summarizeLedger(fields);
  const parts = [`${total} field${total === 1 ? "" : "s"}`];
  if (contested > 0) {
    parts.push(`${contested} contested`);
  }
  if (fills > 0) {
    parts.push(
      fills === 1 ? "1 fills an empty field" : `${fills} fill empty fields`
    );
  }
  const counts = parts.join(" · ");
  return crmLabel ? `${counts} — approvals write to ${crmLabel}` : counts;
};

/*
 * The decision half of the surface: every pending change from every source in
 * one flat list, contested fields first. No tabs - a steward should never have
 * to remember that a second tab might disagree with the one they are reading.
 */
export const ChangeLedger = (props: {
  fields: LedgerField[];
  actions: LedgerRowActions;
  /** Resets the cleared-by-you tracking when the steward moves on. */
  recordId: number;
  /**
   * The CRM vendor's own name - "HubSpot", never the tenant's name for the
   * system - so both the column header and the summary can be concrete about
   * where the steward is looking and where approvals land. Absent for the
   * file and generic system types, which fall back to neutral wording.
   */
  crmLabel?: string;
  /** Names of sources whose fetch failed; the ledger degrades, not blanks. */
  unavailableSources: string[];
  onRetry?: () => void;
  /**
   * The record's applied-change history. Null until it answers - the endpoint's
   * latency is unknown - and the Applied tab only appears once it has, with
   * something in it, so the ledger never waits on it or shows a hollow tab.
   */
  appliedChanges?: AppliedChange[] | null;
}) => {
  const { fields, actions, recordId, crmLabel, unavailableSources, onRetry } =
    props;
  const appliedChanges = props.appliedChanges ?? [];
  const showApplied = appliedChanges.length > 0;
  const [view, setView] = useState<"pending" | "applied">("pending");
  /* Each record opens on its decisions, not the last record's history. */
  useEffect(() => {
    setView("pending");
  }, [recordId]);
  const showingApplied = showApplied && view === "applied";
  const writtenCount = appliedChanges.filter(
    (change) => change.change_status === "WRITTEN"
  ).length;
  const queuedCount = appliedChanges.length - writtenCount;

  /*
   * An empty ledger is two different facts, and a steward working a queue
   * deserves to know which one they are looking at: a record that arrived
   * with nothing pending, or one they just finished deciding. The second is
   * the only completion this surface has, and it used to render as the same
   * flat sentence as the first.
   *
   * The peak count is what the record held before it emptied, so the
   * acknowledgement can say how much work it represents. It resets on
   * recordId because the next record's emptiness is not this one's
   * achievement.
   */
  const peakFieldCount = useRef(0);
  useEffect(() => {
    peakFieldCount.current = 0;
  }, [recordId]);
  if (fields.length > peakFieldCount.current) {
    peakFieldCount.current = fields.length;
  }
  const clearedBySteward = fields.length === 0 && peakFieldCount.current > 0;
  const decidedCount = peakFieldCount.current;

  return (
    <div className={classes.ledger}>
      <div className={classes.ledgerHeader}>
        {showApplied ? (
          /*
           * The heading becomes the switch once there is history to switch
           * to. The count rides on the Applied tab so a steward sees how much
           * Cirrom has already changed on this record without opening it.
           */
          <Tabs
            value={view}
            onChange={(_event, value) => setView(value)}
            aria-label="Record changes"
            sx={{
              minHeight: 0,
              "& .MuiTab-root": {
                minHeight: 0,
                minWidth: 0,
                padding: (theme) => theme.spacing(0.5, 0),
                marginRight: 3,
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 600,
                color: "text.secondary",
                "&.Mui-selected": { color: "text.primary" },
              },
            }}
          >
            <Tab value="pending" label="Pending changes" />
            <Tab
              value="applied"
              aria-label={`Applied changes, ${appliedChanges.length}`}
              label={
                <span>
                  Applied changes
                  <Box
                    component="span"
                    className={classes.ledgerTabCount}
                    sx={{ borderColor: "divider", color: "text.secondary" }}
                  >
                    {appliedChanges.length}
                  </Box>
                </span>
              }
            />
          </Tabs>
        ) : (
          <Typography variant="h4" component="h3">
            Pending changes
          </Typography>
        )}
        {showingApplied ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {[
              writtenCount > 0 ? `${writtenCount} written` : null,
              queuedCount > 0 ? `${queuedCount} not yet written` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={
              fields.length === 0
                ? { color: "text.secondary" }
                : { color: "text.primary", fontWeight: 600 }
            }
          >
            {fields.length === 0
              ? unavailableSources.length > 0
                ? "not known"
                : clearedBySteward
                ? "all decided"
                : "none"
              : buildSummary(fields, crmLabel)}
          </Typography>
        )}
      </div>

      {showingApplied && (
        <AppliedChangesList changes={appliedChanges} crmLabel={crmLabel} />
      )}

      {!showingApplied &&
        unavailableSources.map((source) => (
          <Alert
            key={source}
            severity="warning"
            action={
              onRetry && (
                <Button color="inherit" size="small" onClick={onRetry}>
                  Try again
                </Button>
              )
            }
          >
            Couldn't load {source}. Its changes are missing from this list.
          </Alert>
        ))}

      {showingApplied ? null : fields.length === 0 ? (
        <div className={classes.emptyState}>
          {/*
           * "No pending changes" is a claim, and it is only ours to make when
           * every source answered. With one still missing, the honest state
           * is that we don't know - saying otherwise beside our own failure
           * banner is exactly the surprise the control room exists to avoid.
           */}
          {unavailableSources.length > 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Whether this record has pending changes is unknown until the
              missing source loads.
            </Typography>
          ) : clearedBySteward ? (
            /*
             * The one completion this surface has. Deliberately quiet: a
             * steward clears records all day, so the acknowledgement has to
             * survive the hundredth time - a mark, a count, and the plain
             * fact that the record is now settled. The mark takes the ramp's
             * top band, because a record with nothing outstanding IS the good
             * end of the product's own quality scale.
             */
            <div className={classes.clearedState}>
              <TaskAltIcon
                className={classes.clearedIcon}
                sx={{ color: rampIconColor("Outstanding") }}
              />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Record cleared
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {decidedCount === 1
                    ? "The one change Cirrom had for this record is decided."
                    : `All ${decidedCount} changes Cirrom had for this record are decided.`}
                </Typography>
              </Box>
            </div>
          ) : (
            /*
             * Orient rather than merely report: the first half said nothing
             * is pending and the second half said it again. What a steward
             * actually gains from is knowing this page fills itself.
             */
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Nothing is waiting on you here. Cirrom will surface changes on
              this record as it detects them.
            </Typography>
          )}
        </div>
      ) : (
        <div>
          {/*
           * The two sides of the comparison, named. Without this the ledger
           * is a list that happens to have columns; with it the structure
           * says what the surface is before a single value is read - and it
           * restores the CRM/Cirrom framing both review modals carried.
           */}
          <div className={classes.ledgerColumnHeader}>
            <span />
            {/*
             * Named when we know the vendor: "In HubSpot" tells the steward
             * which window to check, where "In your CRM" only tells them the
             * column is not Cirrom's.
             */}
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {crmLabel ? `In ${crmLabel}` : "In your CRM"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Cirrom proposes
            </Typography>
          </div>
          <Divider />
          {fields.map((field, index) => (
            <React.Fragment key={field.fieldName}>
              {index > 0 && <Divider />}
              <LedgerRow field={field} actions={actions} />
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};
